import { PPQ, barCapacity } from '@simplenotation/theory';
import type { Score, TimeSignature } from '../../model/types';
import { timeSigAt, keySigAt } from '../../model/query';
import { buildTimeline } from '../../timeline';
import { SOUNDS } from '../../model/instruments';
import { gmProgramOf } from '../sounds';
import { writeSmf, meta, metaText, type SmfEvent } from './smf';

/** MIDI channel (0-based) for each track: drums on channel 10 (index 9), others in order skipping 9. */
export function midiChannels(score: Score): number[] {
  const pool = [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15];
  let k = 0;
  return score.tracks.map((t) => (t.instrument === 'drums' || t.sound === 'drums' ? 9 : pool[k++ % pool.length]));
}

/** Time signature describing an actual bar length (pickups, over-full bars). */
function tsForLength(length: number, ts: TimeSignature): { num: number; den: number } {
  if (Math.abs(length - barCapacity(ts)) < 1e-6) return { num: ts.num, den: ts.den };
  for (const den of [ts.den, 4, 8, 16, 32, 64]) {
    const unit = (PPQ * 4) / den;
    const num = length / unit;
    if (Number.isInteger(num) && num >= 1 && num <= 255) return { num, den };
  }
  return { num: ts.num, den: ts.den };
}

/** Standard MIDI File (format 1) bytes for a score: one MTrk per track plus a tempo track. */
export function exportMidi(score: Score): Uint8Array {
  const tl = buildTimeline(score);
  const conductor: SmfEvent[] = [];
  conductor.push(metaText(0, 0x03, score.meta.title || 'Untitled'));
  if (score.meta.copyright) conductor.push(metaText(0, 0x02, score.meta.copyright));
  let lastTs = '';
  let lastKs = '';
  const sectionSeen = new Set<number>();
  for (const pb of tl.bars) {
    const ts = timeSigAt(score, pb.barIdx);
    const eff = tsForLength(pb.length, ts);
    const tsKey = `${eff.num}/${eff.den}`;
    if (tsKey !== lastTs) {
      const dd = Math.round(Math.log2(eff.den));
      const compound = eff.den >= 8 && eff.num % 3 === 0 && eff.num > 3;
      const clocks = Math.max(1, Math.min(255, Math.round((96 / eff.den) * (compound ? 3 : 1))));
      conductor.push(meta(pb.startTick, 0x58, [eff.num & 0xff, dd, clocks, 8], 1));
      lastTs = tsKey;
    }
    const ks = keySigAt(score, pb.barIdx);
    const ksKey = `${ks.fifths}/${ks.mode}`;
    if (ksKey !== lastKs) {
      conductor.push(meta(pb.startTick, 0x59, [ks.fifths & 0xff, ks.mode === 'minor' ? 1 : 0], 1));
      lastKs = ksKey;
    }
    const sec = score.bars[pb.barIdx].section;
    if (sec && !sectionSeen.has(pb.barIdx)) {
      sectionSeen.add(pb.barIdx);
      const label = [sec.letter, sec.name].filter(Boolean).join(' ');
      if (label) conductor.push(metaText(pb.startTick, 0x06, label, 2));
    }
  }
  for (const tp of tl.tempo) {
    const us = Math.max(1, Math.min(0xffffff, Math.round(60_000_000 / tp.qpm)));
    conductor.push(meta(tp.tick, 0x51, [(us >> 16) & 0xff, (us >> 8) & 0xff, us & 0xff], 1));
  }
  const endTick = Math.max(
    Math.round(tl.totalTicks),
    ...tl.events.map((e) => Math.round(e.startTick + e.durTicks)),
  );
  conductor.push(meta(endTick, 0x2f, [], 9));

  const channels = midiChannels(score);
  const tracks: SmfEvent[][] = [conductor];
  score.tracks.forEach((track, ti) => {
    const ch = channels[ti];
    const ev: SmfEvent[] = [];
    ev.push(metaText(0, 0x03, track.name || `Track ${ti + 1}`));
    const sound = SOUNDS.find((s) => s.id === track.sound);
    if (sound) ev.push(metaText(0, 0x04, sound.name));
    if (ch !== 9) ev.push({ tick: 0, data: [0xc0 | ch, gmProgramOf(track.sound) & 0x7f], order: 2 });
    ev.push({ tick: 0, data: [0xb0 | ch, 7, Math.round(Math.max(0, Math.min(1, track.volume ?? 0.8)) * 127)], order: 2 });
    ev.push({ tick: 0, data: [0xb0 | ch, 10, Math.max(0, Math.min(127, Math.round(64 + (track.pan ?? 0) * 63)))], order: 2 });
    // Notes, resolving same-pitch overlaps (a new attack ends the sounding note).
    const notes = tl.events
      .filter((e) => e.track === ti)
      .map((e) => ({
        midi: Math.max(0, Math.min(127, Math.round(e.midi))),
        on: Math.round(e.startTick),
        off: Math.max(Math.round(e.startTick) + 1, Math.round(e.startTick + e.durTicks)),
        vel: Math.max(1, Math.min(127, Math.round(e.velocity * 127))),
      }))
      .sort((a, b) => a.on - b.on || a.midi - b.midi);
    const lastByPitch = new Map<number, (typeof notes)[number]>();
    const kept: typeof notes = [];
    for (const n of notes) {
      const prev = lastByPitch.get(n.midi);
      if (prev && prev.off > n.on) {
        if (prev.on === n.on) {
          prev.off = Math.max(prev.off, n.off);
          continue;
        }
        prev.off = n.on;
      }
      lastByPitch.set(n.midi, n);
      kept.push(n);
    }
    for (const n of kept) {
      ev.push({ tick: n.on, data: [0x90 | ch, n.midi, n.vel], order: 4 });
      ev.push({ tick: n.off, data: [0x80 | ch, n.midi, 0x40], order: 3 });
    }
    ev.push(meta(endTick, 0x2f, [], 9));
    tracks.push(ev);
  });
  return writeSmf(1, PPQ, tracks);
}
